<script setup lang="ts">
const { ApiService } = useApiService();
const { $containment } = useNuxtApp();
const { t } = useTexts();
const { userEmail, fetchUserProfile } = useUserData();
const { openToaster } = useLayoutStore();
const { openForgotPassword } = useAuth();
const { isAuthenticated, recoveryPasswordFlow } = storeToRefs(useAuthStore());
const { isLoading: isLoadingUserData } = storeToRefs(useUserDataStore());
const { emailToChange } = storeToRefs(useUserActivityStore());
const { resetEmailToChange } = useUserActivityStore();
const { closeModal } = useLayoutStore();
const { setAuth, resetRecoveryPasswordFlow } = useAuthStore();
const { isValidationModuleEnabled } = useModulesValidation();

const {
  lastDateEmailSent,
  setLastDateEmailSent,
  isEmailVerified,
  isNeedToValidateEmail,
} = useUserValidations();

const SECONDS_TO_RESEND_CODE = 60;

const typedCode = ref('');
const isLoading = ref(false);

const isValid = computed<boolean>(() => {
  return typedCode.value.length === 5;
});

function onSubmit() {
  if (isChangeEmailFieldVisible.value && !emailToChange.value) {
    onChangeNumberConfirmButtonClick();
  } else {
    onConfirmButtonClick();
  }
}

function handleSuccess(
  res: { status: number; data: { success: boolean; message: string } },
  successCallback?: (res: any) => void,
) {
  if (res.status.toString().startsWith('20') && res.data.success) {
    successCallback?.(res);
  } else {
    const message = res.data.message || t('auth.register.error.status_error');

    openToaster({
      message,
      type: 'error',
    });
  }
}

function handleError(err: any, fallbackMessageKey: string) {
  const errorMessage = err?.data?.message || t(fallbackMessageKey);

  if (err.status === 406) {
    closeModal();
  }

  openToaster({
    message: errorMessage,
    type: 'error',
  });
}

const secondsToResendCode = ref(SECONDS_TO_RESEND_CODE);
const countdownInterval = ref<number | null>(null);

function startCountdown() {
  if (countdownInterval.value) {
    clearInterval(countdownInterval.value);
  }

  const lastDate = lastDateEmailSent.value
    ? new Date(lastDateEmailSent.value).getTime()
    : 0;

  const now = new Date().getTime();
  const elapsedSeconds = Math.floor((now - lastDate) / 1000);
  const remainingSeconds = SECONDS_TO_RESEND_CODE - elapsedSeconds;

  secondsToResendCode.value = remainingSeconds > 0 ? remainingSeconds : 0;

  countdownInterval.value = setInterval(() => {
    if (secondsToResendCode.value > 0) {
      secondsToResendCode.value -= 1;
    } else {
      if (countdownInterval.value !== null) {
        clearInterval(countdownInterval.value);
      }
      countdownInterval.value = null;
      setLastDateEmailSent(null);
    }
  }, 1000) as unknown as number;
}

const canResendCode = computed<boolean>(() => {
  if (!lastDateEmailSent.value) return true;

  return secondsToResendCode.value <= 0;
});

async function sendEmailCode() {
  if (!canResendCode.value) return;

  isLoading.value = true;

  let payload = {};

  if (emailToChange.value) {
    payload = { email: emailToChange.value };
  }
  if (recoveryPasswordFlow.value?.token) {
    payload = { token: recoveryPasswordFlow.value.token };
  }

  try {
    const endpoint = isAuthenticated.value
      ? '/bff/users/send-email'
      : '/auth/passwords/reset/by-email';

    const res = await ApiService.post(endpoint, payload);

    handleSuccess(res, () => {
      setLastDateEmailSent(new Date().toISOString());
      startCountdown();

      openToaster({
        message: t('verification.email.resend_successful'),
        type: 'success',
      });
    });
  } catch (err: any) {
    handleError(err, 'auth.register.error.response_error');

    if (err?.status === 403 && recoveryPasswordFlow.value?.token) {
      await solveEmailContainment();
      resetRecoveryPasswordFlow();
      openForgotPassword();
    }
  } finally {
    isLoading.value = false;
  }
}

async function validateEmailCode() {
  if (!isValid.value) {
    return;
  }

  isLoading.value = true;

  const payload: Record<string, any> = { code: typedCode.value };

  if (emailToChange.value) {
    Object.assign(payload, { email: emailToChange.value });
  } else if (recoveryPasswordFlow.value?.token) {
    Object.assign(payload, {
      token: recoveryPasswordFlow.value.token,
      type: 'email',
    });
  }

  try {
    const endpoint = isAuthenticated.value
      ? '/bff/users/validade-email-code'
      : '/auth/passwords/reset/validate-code';

    const res = await ApiService.post(endpoint, payload);

    handleSuccess(res, async () => {
      const { saveToken } = useJwtStore();

      if (isAuthenticated.value) {
        if (emailToChange.value) {
          saveToken(res.data.access_token);
        }

        await fetchUserProfile({ force: true });

        setLastDateEmailSent(null);
        resetEmailToChange();
      }

      const { setRecoveryPasswordData } = useAuthStore();

      setRecoveryPasswordData({
        code: typedCode.value,
      });

      await nextTick(async () => {
        await solveEmailContainment();
      });
    });
  } catch (err: any) {
    handleError(err, 'auth.register.error.response_error');

    if (err?.status === 403 && recoveryPasswordFlow.value?.token) {
      await solveEmailContainment();
      resetRecoveryPasswordFlow();
      openForgotPassword();
    }
  } finally {
    isLoading.value = false;
  }
}

async function onResendCodeButtonClick() {
  await sendEmailCode();
}

async function onConfirmButtonClick() {
  await validateEmailCode();
}

async function start() {
  startCountdown();

  if (
    (!isNeedToValidateEmail.value && isEmailVerified.value) ||
    !canResendCode.value ||
    isLoading.value
  ) {
    return;
  }

  await sendEmailCode();
}

async function solveEmailContainment() {
  await $containment.solve({
    name: 'email',
    customValidator: () => true,
  });
}

const isChangeEmailFieldVisible = ref(false);

function onChangeEmailButtonClick() {
  isChangeEmailFieldVisible.value = !isChangeEmailFieldVisible.value;
}

const changeEmailValue = ref<string>('');
const isChangedEmail = ref(false);

const disableChangeEmailButton = computed<boolean>(() => {
  return isChangedEmail.value && !canResendCode.value;
});

const emailToShow = computed<string>(() => {
  if (isAuthenticated.value) {
    return userEmail.value;
  }

  return recoveryPasswordFlow.value?.email || '';
});

async function onChangeNumberConfirmButtonClick() {
  if (!changeEmailValue.value) {
    return;
  }

  isLoading.value = true;

  try {
    const res = await ApiService.patch('/bff/users/self-email', {
      email: changeEmailValue.value,
    });

    handleSuccess(res, async () => {
      await setAuth(res);

      nextTick(async () => {
        await fetchUserProfile({ force: true });

        isChangeEmailFieldVisible.value = false;
        isChangedEmail.value = true;

        setLastDateEmailSent(null);
        sendEmailCode();
      });
    });
  } catch (err) {
    handleError(err, 'auth.register.error.response_error');
  } finally {
    setTimeout(() => {
      isLoading.value = false;
    }, 1000);
  }
}

watch(
  isLoadingUserData,
  async (newVal, oldVal) => {
    if (!newVal) {
      const now = new Date().getTime();
      const lastUpdate = oldVal ? new Date(String(oldVal)).getTime() : 0;
      const timeDifference = now - lastUpdate;

      if (timeDifference > 2000) {
        await start();
      }
    }
  },
  { immediate: true },
);

onMounted(async () => {
  await start();
});
</script>

<template>
  <div :class="$style.boxContainer">
    <BaseLoading
      class="!bg-bg-primary !absolute"
      :icon-loader="true"
      :show="isLoading"
    />

    <img
      alt="Email icon"
      aria-hidden="true"
      :class="$style.modalIcon"
      :src="getAssetsUrlForCDN('/assets/images/svg/email.svg')"
    />

    <template v-if="isChangeEmailFieldVisible && !emailToChange">
      <p :class="$style.title">
        {{ $t('verification.email.change_email') }}
      </p>

      <p
        :class="[$style.description, 'max-w-full']"
        v-html="
          $t('verification.email.change_desc', {
            current_email: emailToShow,
          })
        "
      />

      <div :class="$style.changeEmailField">
        <BaseFormInput
          id="email"
          v-model="changeEmailValue"
          autocomplete="off"
          :info="$t('multiStepRegister.email_info')"
          name="email"
          placeholder="ex: alguem@email.com"
          :required="true"
          type="email"
        />
      </div>

      <div :class="$style.buttonsContainer">
        <button
          :class="$style.backBtn"
          @click.prevent="onChangeEmailButtonClick"
        >
          {{ $t('general.cancel') }}
        </button>

        <button
          :class="[$style.submitBtn, 'btn-primary']"
          :disabled="!changeEmailValue"
          @click.prevent="onChangeNumberConfirmButtonClick"
        >
          {{ $t('general.confirm') }}
          <NuxtIcon name="arrows/angle_right" />
        </button>
      </div>
    </template>

    <template v-else>
      <p :class="$style.title">
        {{ $t('verification.email.title') }}
      </p>

      <p
        :class="[$style.description, 'max-w-56']"
        v-html="$t('verification.email.desc')"
      />

      <span :class="$style.email">
        {{ emailToChange || emailToShow }}
      </span>

      <BaseFormInputVerificationCode
        v-model="typedCode"
        @submit="onSubmit"
      />

      <p :class="$style.noCode">
        {{ $t('verification.email.no_code') }}
      </p>

      <button
        :class="$style.resendCode"
        :disabled="!canResendCode"
        @click.prevent="onResendCodeButtonClick"
      >
        {{ $t('verification.resend_code') }}
        {{ canResendCode ? '' : `${$t('general.in')} ${secondsToResendCode}s` }}
      </button>

      <button
        v-if="
          !emailToChange &&
          isValidationModuleEnabled('global') &&
          isAuthenticated
        "
        :class="$style.changeEmail"
        :disabled="disableChangeEmailButton"
        @click.prevent="onChangeEmailButtonClick"
      >
        {{ $t('verification.email.change_email') }}
        {{
          disableChangeEmailButton
            ? `${$t('general.in')} ${secondsToResendCode}s`
            : ''
        }}
      </button>

      <div :class="$style.buttonsContainer">
        <button
          :class="[$style.submitBtn, 'btn-primary']"
          :disabled="!isValid"
          @click="onConfirmButtonClick"
        >
          {{ $t('verification.confirm_code') }}
          <NuxtIcon name="arrows/angle_right" />
        </button>
      </div>
    </template>
  </div>
</template>

<style module lang="scss">
.boxContainer {
  @apply p-3 md:p-6 flex flex-col justify-center flex-1 text-texts text-center;

  .modalIcon {
    @apply w-28 h-28 mx-auto mb-5;
  }

  .title {
    @apply font-bold mb-3;
  }

  .description {
    @apply mb-1 inline-block mx-auto text-sm text-texts/80;

    strong {
      @apply text-primary/80;
    }
  }

  .email {
    @apply text-sm mb-6 font-bold text-texts/70 pointer-events-none;
  }

  .changeEmailField {
    @apply w-full mt-4;
  }

  .noCode {
    @apply mt-5 font-medium text-sm;
  }

  .resendCode {
    @apply text-xs text-primary p-1 font-bold inline-flex mx-auto;

    &:disabled:not([disabled='false']) {
      @apply opacity-40 cursor-not-allowed;
    }
  }

  .changeEmail {
    @apply text-xs text-texts/50 p-1 font-bold inline-flex mx-auto;
  }

  .buttonsContainer {
    @apply flex items-center justify-center gap-2 mx-auto mt-5;

    button {
      @apply rounded-md gap-1.5 flex items-center font-bold uppercase h-10 focus:outline-none focus:ring-4;
    }

    .submitBtn {
      @apply py-5;
    }

    .backBtn {
      @apply bg-auth-texts/10 text-auth-texts/70 px-4 text-xs;
    }
  }
}
</style>
